
import React, { Fragment, ReactElement } from 'react';
import { useIntl } from 'react-intl';

// Components

import SubHeader from '@components/ssap/headers/SubHeader';
import SummaryItem from '@components/ssap/pages/pageIncludes/summary/SummaryItem';

// Utils

import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';

// Constants

import { RENEWALS_REJECTION } from '@constants/ssap/hhmTypes/dropdownOptions';

// Types

import {
    Attestations,
    HouseholdMember,
    IExchangeProgramNames,
    IsState,
    SsapData,
    VoterRegistration
} from '@components/ssap/SsapTypes';

import { flowTypes as FLOW_TYPES, SsapFlowType } from '@constants/ssap/flows/flowTypes';
import states from '@constants/configs/states';

interface IAttestationsSummary {
    data: SsapData;
    exchangeName: string;
    flowType: SsapFlowType;
    isState: IsState;
    exchangeAndProgramNames: IExchangeProgramNames;
}

export const otherAgreementsConfig = [
    {
        attestation: Attestations.AutoReEnrollmentAttestation,
        label: 'ssap.agreement.auto_reenroll_sopa',
        value: 'turquoiseConsent',
        renderFalseValue: true
    },
    {
        attestation: Attestations.CoverageEndAttestation,
        label: 'ssap.agreement.coverage_end',
        value: 'agreeToEndCoverage'
    },
    {
        attestation: Attestations.ReceivedNoticeOfRightsAttestation,
        label: 'ssap.agreement.received_notice_of_rights',
        value: 'receivedNoticeOfRights'
    },
    {
        attestation: Attestations.DataSharingConsentAttestation,
        label: 'ssap.agreement.consent_to_share_data',
        value: 'dataSharingConsent'
    },
    {
        attestation: Attestations.FraudInvestigationAttestation,
        label: 'ssap.agreement.consent_to_fraud_investigation',
        value: 'fraudInvestigation'
    },
    {
        attestation: Attestations.OutsideParentAttestation,
        label: 'ssap.agreement.outside_parent',
        value: 'medicaidConsent'
    },
    {
        attestation: Attestations.MedicaidContactAttestation,
        label: 'ssap.agreement.medicaid_contact',
        value: 'medicaidContactAgreementIndicator'
    },
    {
        attestation: Attestations.ReadyReportChangesAttestation,
        label: 'ssap.agreement.ready_report_changes',
        value: 'changeInformationAgreementIndicator'
    },
    {
        attestation: Attestations.MedicaidCooperationAttestation,
        label: 'ssap.agreement.medicaid_cooperation',
        value: 'medicaidRequirementAgreementIndicator'
    },
    {
        attestation: Attestations.AgreeToFileAttestation,
        label: 'ssap.agreement.agree_to_file_taxes',
        value: 'aptcAgreementIndicator'
    },
    {
        attestation: Attestations.MoneyPursueAttestation,
        label: 'ssap.agreement.money_pursue',
        value: 'agreeToMoneyPursual'
    },
    {
        attestation: Attestations.ClaimNoticeAttestation,
        label: 'ssap.agreement.claim_notice',
        value: 'claimNoticeDMAHSAcknowledgment'
    },
    {
        attestation: Attestations.TruthAttestation,
        label: 'ssap.agreement.truth',
        value: 'isTruthful'
    },
    {
        attestation: Attestations.PenaltyPerjuryAttestation,
        label: 'ssap.agreement.penalty_perjury',
        value: 'penaltyOfPerjuryAgreementIndicator'
    }
];

const AttestationsSummary = (props: IAttestationsSummary): ReactElement => {

    const {
        data: {
            agreeToEndCoverage,
            agreeToUseIncomeData,
            claimNoticeDMAHSAcknowledgment,
            incarcerationAsAttestedIndicator,
            legalAttestations: {
                agreeToMoneyPursual,
                aptcAgreementIndicator,
                changeInformationAgreementIndicator,
                dataSharingConsent,
                fraudInvestigation,
                isTruthful,
                medicaidContactAgreementIndicator,
                medicaidRequirementAgreementIndicator,
                penaltyOfPerjuryAgreementIndicator,
                receivedNoticeOfRights,
                turquoiseConsent,
                voterRegistration
            },
            numberOfYearsAgreed,
            taxHousehold: {
                householdMember,
                medicaidConsent
            }
        },
        exchangeName,
        flowType,
        isState,
        exchangeAndProgramNames
    } = props;
    const { exchangeMedicaid, exchangeMedicaidOrChip } = exchangeAndProgramNames;
    const values = {
        agreeToEndCoverage,
        agreeToMoneyPursual,
        aptcAgreementIndicator,
        changeInformationAgreementIndicator,
        claimNoticeDMAHSAcknowledgment,
        dataSharingConsent,
        fraudInvestigation,
        isTruthful,
        medicaidConsent,
        medicaidContactAgreementIndicator,
        medicaidRequirementAgreementIndicator,
        penaltyOfPerjuryAgreementIndicator,
        receivedNoticeOfRights,
        turquoiseConsent
    };

    const intApi = useIntl();

    const isIdahoNonFinancialFlow = isState.ID && !(flowType === SsapFlowType.FINANCIAL_FLOW);
    const isFinancialFlow: boolean = flowType === FLOW_TYPES.FINANCIAL_FLOW;

    const replacements = {
        exchangeMedicaidOrChip,
        exchangeMedicaid,
        exchangeName,
        yearsQty: numberOfYearsAgreed,
        br: <br />
    };

    interface IncarcerationInfo {
        incarceratedList: Array<string>;
        pendingDispositionList: Array<string>;
    }

    const getIncarcerationInfo = () => {
        const incarcerationInfo: IncarcerationInfo = householdMember.reduce(
            (result: IncarcerationInfo, hhm: HouseholdMember) => {
                const {
                    name,
                    incarcerationStatus: {
                        incarcerationPendingDispositionIndicator,
                        incarcerationStatusIndicator
                    }
                } = hhm;

                const personName = normalizeNameOfPerson(name);

                if (incarcerationPendingDispositionIndicator === true) {
                    result = {
                        ...result,
                        pendingDispositionList: [
                            ...result.pendingDispositionList,
                            personName
                        ]
                    };
                }

                if (incarcerationStatusIndicator === true) {
                    result = {
                        ...result,
                        incarceratedList: [
                            ...result.incarceratedList,
                            personName
                        ]
                    };
                }

                return result;
            },
            {
                incarceratedList: [ ],
                pendingDispositionList: [ ]
            } as IncarcerationInfo
        );

        const {
            incarceratedList,
            pendingDispositionList
        } = incarcerationInfo;

        return (
            <Fragment>
                {
                    incarcerationAsAttestedIndicator
                        ? (
                            <SummaryItem
                                textCode = 'ssap.agreement.incarcerated'
                                value = 'ssap.agreement.nobody_is_incarcerated'
                            />
                        )
                        : (
                            <Fragment>
                                {
                                    incarceratedList.length > 0
                                        ? (
                                            <SummaryItem
                                                ignoreIntl
                                                textCode = 'ssap.agreement.incarcerated'
                                                value = { incarceratedList.join(', ') }
                                            />
                                        )
                                        : null
                                }
                                {
                                    pendingDispositionList.length > 0
                                        ? (
                                            <SummaryItem
                                                ignoreIntl
                                                textCode = 'ssap.agreement.with_pending_disposition'
                                                value = { pendingDispositionList.join(', ') }
                                            />
                                        )
                                        : null
                                }
                            </Fragment>
                        )
                }
            </Fragment>
        );
    };

    const getRenewalAgreements = () => {

        const getNumberOfYears = () => {
            if (numberOfYearsAgreed === RENEWALS_REJECTION) {
                let id = 'ssap.agreement.number_of_years.no_qty';

                if (isIdahoNonFinancialFlow) {
                    id = 'ssap.agreement.number_of_years.no_qty_nonfa';
                }

                return intApi.formatMessage(
                    { id },
                    replacements
                );
            }

            return intApi.formatMessage(
                { id: 'ssap.agreement.number_of_years.qty' },
                replacements
            );
        };

        const getTextCodeForNumberOfYears = () => {
            let textCode = 'ssap.agreement.number_of_years';

            if (isIdahoNonFinancialFlow) {
                textCode = 'ssap.agreement.number_of_years_nonfa';
            }

            return textCode;
        };

        const getIncomeDataAgreements = () => {

            const agreeValueForVirginia = intApi.formatMessage(
                { id: 'ssap.agreement.custom_agree.income_data_usage' },
                replacements,
            );

            const getTextCodeBasedOnFlowType = (): string => {
                const statesWithNonFinancialFlowText = [states.ILLINOIS, states.NEW_MEXICO];
                const isStateWithNonFinancialFlowText = statesWithNonFinancialFlowText.some(
                    stateKey => isState[stateKey]
                );
                const shouldUseNonFinancialFlowText = isStateWithNonFinancialFlowText && !isFinancialFlow;
                
                return shouldUseNonFinancialFlowText
                    ? 'ssap.agreement.to_save_time_question'
                    : 'ssap.agreement.income_data_usage';
            };

            const textCodeForFlow = getTextCodeBasedOnFlowType();

            switch (true) {
                case isState.PA:
                case isState.MN:
                    return null;
                case isState.GI && !!agreeToUseIncomeData:
                case isState.VA && !!agreeToUseIncomeData:
                    return (
                        <SummaryItem
                            ignoreIntl
                            replacements = { replacements }
                            textCode = 'ssap.agreement.income_data_usage'
                            value = { agreeValueForVirginia }
                        />
                    );
                default:
                    return (
                        <SummaryItem
                            booleanAsAgree
                            replacements = { replacements }
                            textCode = { textCodeForFlow }
                            value = { agreeToUseIncomeData }
                        />
                    );
            }
        };

        const getIfNumberOfYearsShown = () => {
            const hasDisagreedToUseIncomeData = agreeToUseIncomeData === false;

            switch (true) {
                case isState.MN:
                    return isFinancialFlow;
                case isState.PA:
                    return true;
                case hasDisagreedToUseIncomeData && !isState.NJ:
                    return true;
                default:
                    return false;
            }
        };
        return (
            <Fragment>
                {
                    getIncomeDataAgreements()
                }
                {
                    (getIfNumberOfYearsShown())
                        ? (
                            <SummaryItem
                                ignoreIntl
                                replacements = { replacements }
                                textCode = { getTextCodeForNumberOfYears() }
                                value = { getNumberOfYears() }
                            />
                        )
                        : null
                }
            </Fragment>
        );
    };

    const getOtherAgreements = () => {
        return otherAgreementsConfig.reduce(
            (agreements, agreement) => {
                const {
                    label,
                    renderFalseValue,
                    value
                } = agreement;

                const resolvedValue = values[value];
                const falseFoundAndShouldBeRendered = renderFalseValue && resolvedValue === false;

                if (resolvedValue || falseFoundAndShouldBeRendered) {

                    const agreementSummaryItem = (
                        <SummaryItem
                            booleanAsAgree
                            key = { label }
                            replacements = { replacements }
                            textCode = { label }
                            value = { resolvedValue }
                        />
                    );

                    return [
                        ...agreements,
                        agreementSummaryItem
                    ];
                } else {
                    return agreements;
                }
            },
            [ ]
        );
    };

    const getVoterRegistrationSelection = () => {
        if (voterRegistration === VoterRegistration.UNSPECIFIED || !isState.PA) {
            return null;
        }

        const getValue = () => {
            switch (voterRegistration) {
                case VoterRegistration.APPLYING_FOR_REGISTRATION:
                    return true;
                case VoterRegistration.NOT_APPLYING_FOR_REGISTRATION:
                    return false;
                case VoterRegistration.ALREADY_REGISTERED:
                    return 'ssap.agreement.already_registered';
                default:
                    return null;
            }
        };

        return (
            <SummaryItem
                booleanAsAgree
                textCode = 'ssap.agreement.voter_registration'
                value = { getValue() }
            />
        );
    };

    return (
        <div className = 'summary__block margin-b-20' >
            <div className = 'summary__header' >
                <SubHeader
                    headerClassName = 'gi-heading-alt'
                    text = 'ssap.subheader.agreements'
                />
            </div>
            { getIncarcerationInfo() }
            { getRenewalAgreements() }
            { getOtherAgreements() }
            { getVoterRegistrationSelection() }
        </div>
    );
};

export default AttestationsSummary;